import { differenceInDays, differenceInHours, differenceInMinutes, differenceInWeeks } from "date-fns";
import React, { ReactNode } from "react";
import { RCCHookToast, RCCIcon, RCCTooltip } from "react-common-components";
import { Link } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import { AppIconTypes } from "src/components/AppIcons/interface";
import Photo from "src/components/Photo";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import iconScore from "src/assets/imgs/score-icon.png";
import { INotificationCard, INotificationsMessage, INotificationsSystemMessage, MessageOrigin, MessageType } from "./interface";
import ContainerNotificationCard, { MessageContent, NotificationIconContainer, SystemImg } from "./styles";

const differenceBetweenDatesInString = (date: Date, translate: (text: string, objKeys?: object | undefined) => string) => {
	const today = new Date();
	const dateCreated = new Date(date);
	const weeksDifference = differenceInWeeks(today, dateCreated);
	
	if(weeksDifference >= 1)
		return `${weeksDifference} ${translate("semana(s)")}`;


	const numberDays = differenceInDays(today, dateCreated);

	if(numberDays === 0){
		const numberHours = differenceInHours(today, dateCreated);
		if(numberHours === 0){
			const numberMinutes = differenceInMinutes(today, dateCreated);
			
			if(numberMinutes === 0)
				return translate("Agora");

			return `${numberMinutes} ${translate("minutos")}`;
		}
		return `${numberHours} ${translate("horas")}`;
	}
	
	return `${numberDays} ${translate("dias")}`;
};

const returnColorNotification = (type: MessageType) => {
	if(type === MessageType.SUCCESS)
		return "green";
	if(type === MessageType.INFO)
		return "blue";
	if(type === MessageType.WARNING)
		return "yellow";
	return "red";
};

const getAppIcon = (origin: MessageOrigin) => {
	if(origin === MessageOrigin.UNDEFINED)
		return null;

	let iconName: AppIconTypes = "actions";

	switch(origin){
	case MessageOrigin.INDICATOR:{
		iconName = "indicators";
		break;
	}
	case MessageOrigin.OKR:{
		iconName = "strategicPlanning";
		break;
	}
	case MessageOrigin.MEETING:{
		iconName = "meeting";
		break;
	}
	case MessageOrigin.OCCURRENCE:{
		iconName = "occurrences";
		break;
	}
	case MessageOrigin.PROJECT:{
		iconName = "projects";
		break;
	}
	case MessageOrigin.MAP:{
		iconName = "strategicPlanning";
		break;
	}
	case MessageOrigin.FINANCIAL_MANAGEMENT:{
		iconName = "financialManagement";
		break;
	}
	case MessageOrigin.AUDIT:{
		iconName = "audits";
		break;
	}
	case MessageOrigin.RISK:{
		iconName = "risks";
		break;
	}
	case MessageOrigin.ANALYSIS:{
		iconName = "analysis";
		break;
	}
	case MessageOrigin.SWOT_ANALYSIS:{
		iconName = "analysis";
		break;
	}
	case MessageOrigin.ANALYSIS_MARKET_SHARE:{
		iconName = "analysis";
		break;
	}
	case MessageOrigin.SUGGESTIONS:{
		iconName = "configs";
		break;
	}
	case MessageOrigin.CONTRACTS:{
		iconName = "configs";
		break;
	}
	default:{
		break;
	}
	}

	return <AppIcons 
		name={iconName}
		size={14}
		className="app-icons"
	/>;
};

const getLink = (origin: MessageOrigin, originId: number | null, dontMountLink: boolean, children: ReactNode) => {
	if(origin === MessageOrigin.UNDEFINED || dontMountLink)
		return <>{children}</>;

	let link = "";
	switch(origin){
	case MessageOrigin.ACTION:
		link = "/actions/panel";
		break;
	case MessageOrigin.INDICATOR:
		link = "/indicators/panel";
		break;
	case MessageOrigin.OKR:
		link = "/okr/panel";
		break;
	case MessageOrigin.OCCURRENCE:
		link = "/occurrences/panel";
		break;
	case MessageOrigin.PROJECT:
		link = "/projects/panel";
		break;
	case MessageOrigin.MEETING:
		link = "/meeting/panel";
		break;
	case MessageOrigin.MAP:
		link = "/maps/panel";
		break;
	case MessageOrigin.FINANCIAL_MANAGEMENT:
		link = "/financialmanagement/panel";	
		break;
	case MessageOrigin.AUDIT:
		link = "/audit/panel";
		break;
	case MessageOrigin.RISK:
		link = "/risks/panel";
		break;
	case MessageOrigin.ANALYSIS:
		link = "/analysis/panel";
		break;
	case MessageOrigin.SWOT_ANALYSIS:
		link = "/analysis/swot";
		break;
	case MessageOrigin.ANALYSIS_MARKET_SHARE:
		link = "/analysis/marketshare";
		break;
	case MessageOrigin.SUGGESTIONS:
		link = "/suggestions";
		break;
	case MessageOrigin.CONTRACTS:
		link = "/monitorcontracts";
		break;
	default: 
		break;
	}

	if(link.length === 0)
		return <>{children}</>;
	if(originId !== null)
		link += `/${originId}`;

	return <Link to={{
		pathname: link,
		state: {
			refresh: true
		}
	}}>
		{children}
	</Link>;	
};

const returnIconOfNotification = (id: number, isRead: boolean, isSystem: boolean,  userImage: string, userName: string, translate: (text: string, objKeys?: object | undefined) => string, toggleRead: (id: number, isRead: boolean, isSystem: boolean) => void) => <RCCTooltip 
	color="grey" 
	text={isRead ? translate("Marcar como não lido") : translate("Marcar como lido")}
	position="right"
	styleInline="width: 120px;"
>
	<NotificationIconContainer onClick={() => toggleRead(id, !isRead, isSystem)}>			
		<RCCIcon 
			name={isRead ? "mailOpen" : "mail"}
			color="var(--input-font-color)"
			className="icon-mark-as-read"
		/>		
		{isSystem ? 
			<SystemImg src={iconScore} alt="Home Page" />
			: 
			<Photo
				width={42}
				height={42}
				hover={false}
				name={userName || "System"}
				link={userImage}
			/>
		}
	</NotificationIconContainer>
</RCCTooltip>;

const NotificationCard: React.FC<INotificationCard> = ({
	notification,
	toggleRead,
	openSystemNotification,
	closeDrawer
}) => {
	const {REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY} = process.env;
	const { addToast } = RCCHookToast();
	const { company } = useAuth();
	const { translate, currentLanguage } = useTranslation();

	const getMessage = () => {
		try{
			if(notification.isSystem){
				const messageObj: INotificationsSystemMessage = JSON.parse(notification.message);
				return messageObj[currentLanguage];
			}
			const messageObj: INotificationsMessage = JSON.parse(notification.message);
			return translate(messageObj.text, {
				...messageObj.binds,
				user: notification.userName,
				date: formatDate(notification.dateCreated, currentLanguage, true),
				company: notification.companyName
			});
		}catch(err){
			return translate(notification.message);
		}
	};

	const getFullMessage = () => {
		try{
			if(notification.isSystem){
				const messageObj: INotificationsSystemMessage = JSON.parse(notification.fullMessage);
				return messageObj[currentLanguage].replace(/<[^>]*>/g, "").replaceAll("&nbsp;", "");
			}
			return translate(notification.fullMessage);
		}catch(err){
			return translate(notification.fullMessage);
		}
	};

	const title = getMessage();

	return (
		<ContainerNotificationCard isRead={notification.isRead} color={returnColorNotification(notification.type)}>
			{returnIconOfNotification(
				notification.id, 
				notification.isRead,
				notification.isSystem, 
				notification.userImage ? `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${notification.userImage}` : "", 
				notification.userName, 
				translate, 
				toggleRead
			)}
			{getLink(
				notification.origin,
				notification.originId,
				notification.companyId !== null && company.efa002Id !== notification.companyId,
				<MessageContent onClick={() => {
					if(notification.isSystem && !(notification.id === 1 || notification.id === 2 || notification.id === 3)){
						openSystemNotification(notification);
						if(notification.isRead)
							closeDrawer();
					}
					if(notification.companyId !== null && company.efa002Id !== notification.companyId){
						addToast({
							title: translate("Aviso"),
							description: translate("Essa notificação pertence a outra empresa, acesse-a para verificar"),
							color: "yellow"
						});
						return;
					}
					if(notification.isRead)
						return;
					toggleRead(notification.id, true, notification.isSystem);
					closeDrawer();
				}}>
					<span className="title-message-notification" title={title}>
						{title}
					</span>
					<small>
						{getAppIcon(notification.origin)}
						{differenceBetweenDatesInString(notification.dateCreated, translate)}
						{notification.fullMessage !== null && notification.fullMessage.length > 0 && ` - ${getFullMessage()}`}
					</small>
					{notification.companyId !== null && company.efa002Id !== notification.companyId && <span className="business-group-div">
						<RCCIcon 
							name="building"
							color="var(--input-font-color)"
						/>
						{notification.companyName}
					</span>}
				</MessageContent>
			)}
		</ContainerNotificationCard>
	);
};

export default NotificationCard;
